import React, { useEffect, useMemo, useState } from 'react'
import styles from './MainPage.module.scss'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { TopInterval } from '@enums/top.interval'
import { ITableState } from '@utils/sort'
import { useGetUserList } from '@api/tasksCrmApi'
import Box from '@components/ui/Box/Box'
import TopUsers from '@components/TopUsers/TopUsers'
import InfoCard from '@components/InfoCard/InfoCard'
import { routersPath } from '@router/routes-path'
import CustomTable from '@components/ui/CustomTable/CustomTable'
import { usersTableColumns } from './usersTable.columns'
import { useMainStatApi } from '@api/useMainStatApi'
import { useRedirect } from '@hooks/useRedirect'
import { roundNumber } from '@utils/stringUtils'
import HeaderSelect from '@components/HeaderSelect/HeaderSelect'
import { IconDollarReceiveSquare, IconDollarSendSquare, IconSackDollar } from '@utils/icons'
import { getNoun } from '@utils/getNoun'

export const initialDate = [
  moment(Date.now()).startOf('week').format('YYYY-MM-DDTHH:mm:ss[Z]'),
  moment(Date.now()).endOf('week').format('YYYY-MM-DDTHH:mm:ss[Z]'),
]

export const initialPagination = {
  sort: {
    sort: null,
    field: null,
  },
  pagination: {
    current: 1,
    pageSize: 10,
  },
}

const MainPage: React.FC = () => {
  const navigate = useNavigate()
  const { userRedirect, crmRedirect } = useRedirect()
  const [selectedCrms, setSelectedCrms] = useState<string[]>([])
  const [selectedWeek, setSelectedWeek] = useState<string[]>(initialDate)
  const [tableDate, setTableDate] = useState<string[] | 'all' | undefined>(initialDate)
  const [tableInterval, setTableInterval] = useState<TopInterval>(TopInterval.WEEK)
  const [state, setState] = useState<ITableState>(initialPagination)
  const [isAllCrms, setIsAllCrms] = useState(false)

  const {
    topWeek,
    topMonth,
    topAll,
    totalExpenses,
    totalIncomes,
    totalSalary,
    isLoading: isStatLoading,
  } = useMainStatApi({
    dates: selectedWeek,
    crmId: selectedCrms,
    isAllCrms,
  })

  const crmSelectHandler = (e: string[]) => {
    setState(initialPagination)
    setSelectedCrms(e)
  }

  useEffect(() => {
    setTableDate(selectedWeek)
  }, [selectedWeek])

  const selectedMonth = useMemo(() => {
    return selectedWeek && selectedWeek.length
      ? [
          moment(selectedWeek[0]).startOf('month').format('YYYY-MM-DDTHH:mm:ss[Z]'),
          moment(selectedWeek[0]).endOf('month').format('YYYY-MM-DDTHH:mm:ss[Z]'),
        ]
      : undefined
  }, [selectedWeek])

  const { rows: currentTable, count } = useGetUserList({
    dates: tableDate ?? selectedWeek,
    id: selectedCrms,
    sort: state.sort,
    pagination: state.pagination,
    isAllCrms,
  })

  const currentHandler = (interval?: TopInterval) => {
    switch (interval) {
      case TopInterval.WEEK: {
        setTableInterval(TopInterval.WEEK)
        setTableDate(selectedWeek)
        break
      }
      case TopInterval.MONTH: {
        setTableInterval(TopInterval.MONTH)
        setTableDate(selectedMonth)
        break
      }
      case TopInterval.ALL_TIME: {
        setTableInterval(TopInterval.ALL_TIME)
        setTableDate('all')
        break
      }
      default: {
        setTableInterval(TopInterval.WEEK)
        setTableDate(selectedWeek)
      }
    }
  }

  const mappedTableData = useMemo(() => {
    return currentTable
      ? currentTable.map((el) => ({
          ...el,
          key: el.userid,
          nick_name: (
            <span className={styles.tableLink} onClick={() => userRedirect(el.userid)}>
              {el.nick_name} <small>({el.login})</small>
            </span>
          ),
          crmname: (
            <span className={styles.tableLink} onClick={() => crmRedirect(el.crmid, el?.crmname)}>
              {el.crmname}
            </span>
          ),
        }))
      : undefined
  }, [currentTable])

  const periodLabel = useMemo(() => {
    switch (tableInterval) {
      case TopInterval.WEEK:
        return 'Неделя'
      case TopInterval.MONTH:
        return 'Месяц'
      case TopInterval.ALL_TIME:
        return 'Все время'
      default:
        return 'Неделя'
    }
  }, [tableInterval])

  return (
    <>
      <Box className={styles.container}>
        <>
          <div className={styles.header}>Общая статистика</div>
          <HeaderSelect
            crmSelectHandler={crmSelectHandler}
            setSelectedWeek={setSelectedWeek}
            selectedWeek={selectedWeek[0]}
            setIsAllCrms={setIsAllCrms}
          />
          <div className={styles.topRow}>
            <TopUsers
              userLink={userRedirect}
              crmLink={crmRedirect}
              users={topWeek}
              active={tableInterval === TopInterval.WEEK}
              title='Топ за неделю'
              onClick={() => currentHandler(TopInterval.WEEK)}
              isLoading={isStatLoading}
            />
            <TopUsers
              userLink={userRedirect}
              crmLink={crmRedirect}
              users={topMonth}
              active={tableInterval === TopInterval.MONTH}
              title='Топ за месяц'
              onClick={() => currentHandler(TopInterval.MONTH)}
              isLoading={isStatLoading}
            />
            <TopUsers
              userLink={userRedirect}
              crmLink={crmRedirect}
              users={topAll}
              active={tableInterval === TopInterval.ALL_TIME}
              title='Топ за все время'
              onClick={() => currentHandler(TopInterval.ALL_TIME)}
              isLoading={isStatLoading}
            />
          </div>
        </>
      </Box>
      <Box className={styles.containerStat}>
        <div className={styles.tableHeader}>
          {`Статистика за выбранный период: ${periodLabel}`}
          <span>
            Всего:{' '}
            <s>
              {count ? count : '0'} {getNoun(Number(count), 'менеджер', 'менеджера', 'менеджеров')}
            </s>
          </span>
        </div>
        <CustomTable
          columns={usersTableColumns}
          dataSource={mappedTableData}
          allCount={count}
          updatePagination={setState}
          pagination={state.pagination}
        />
      </Box>
      <Box className={styles.container}>
        <div className={styles.header}>Cтатистика по бухгалтерии</div>
        <div className={styles.topRow}>
          <InfoCard className={styles.hover}>
            <div
              onClick={() =>
                navigate(routersPath.INCOMES, {
                  state: {
                    selectedCrms: selectedCrms,
                    selectedWeek: selectedWeek,
                  },
                })
              }
            >
              <div className={styles.title}>
                Тотал за неделю
                <span>
                  <IconSackDollar width='24' height='24' fill='var(--color-green)' />
                </span>
              </div>
              <div className={styles.list}>
                <div className={styles.rowHeader}>
                  <span className={styles.headerItem}>Система</span>
                  <span className={styles.headerItem}>Сумма</span>
                </div>
                <div className={styles.row}>
                  <div className={styles.userTitle}>Bitcoin (BTC)</div>
                  <div>
                    {roundNumber(totalIncomes?.totalBtc, 2)} <small>BTC</small>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.userTitle}>Tether (USDT)</div>
                  <div>
                    {roundNumber(totalIncomes?.totalUsdt, 2)} <small>USDT</small>
                  </div>
                </div>
                <div className={styles.rowTotal}>
                  <div className={styles.userTitle}>Всего</div>
                  <div>
                    {roundNumber(totalIncomes?.totalAll)} <small>USDT</small>
                  </div>
                </div>
              </div>
            </div>
          </InfoCard>
          <InfoCard className={styles.hover}>
            <div
              onClick={() =>
                navigate(routersPath.EXPENSES, {
                  state: {
                    selectedCrms: selectedCrms,
                    selectedWeek: selectedWeek,
                  },
                })
              }
            >
              <div className={styles.title}>
                Расходы за неделю
                <span>
                  <IconDollarSendSquare width='24' height='24' fill='var(--color-green)' />
                </span>
              </div>
              <div className={styles.list}>
                <div className={styles.rowHeader}>
                  <span className={styles.headerItem}>Система</span>
                  <span className={styles.headerItem}>Сумма</span>
                </div>
                <div className={styles.row}>
                  <div className={styles.userTitle}>Bitcoin (BTC)</div>
                  <div>
                    {roundNumber(totalExpenses?.totalBtc)} <small>BTC</small>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.userTitle}>Tether (USDT)</div>
                  <div>
                    {roundNumber(totalExpenses?.totalUsdt)} <small>USDT</small>
                  </div>
                </div>
                <div className={styles.rowTotal}>
                  <div className={styles.userTitle}>Всего</div>
                  <div>
                    {roundNumber(totalExpenses?.totalAll)} <small>USDT</small>
                  </div>
                </div>
              </div>
            </div>
          </InfoCard>
          <InfoCard className={styles.hover}>
            <div
              onClick={() =>
                navigate(routersPath.SALARY, {
                  state: {
                    selectedCrms: selectedCrms,
                    selectedWeek: selectedWeek,
                  },
                })
              }
            >
              <div className={styles.title}>
                Осталось выплатить
                <span>
                  <IconDollarReceiveSquare width='24' height='24' fill='var(--color-green)' />
                </span>
              </div>
              <div className={styles.list}>
                <div className={styles.rowHeader}>
                  <span className={styles.headerItem}>Кому</span>
                  <span className={styles.headerItem}>Сумма</span>
                </div>
                <div className={styles.row}>
                  <div className={styles.userTitle}>Клоузеры</div>
                  <div>
                    {roundNumber(totalSalary?.closer)} <small>USDT</small>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.userTitle}>Менеджеры</div>
                  <div>
                    {roundNumber(totalSalary?.managers)} <small>USDT</small>
                  </div>
                </div>
                <div className={styles.rowTotal}>
                  <div className={styles.userTitle}>Всего</div>
                  <div>
                    {roundNumber(totalSalary?.totalAll)} <small>USDT</small>
                  </div>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>
      </Box>
    </>
  )
}

export default MainPage
